import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef, useState, type FormEvent, type ReactNode } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { PageHeader } from "../components/AppShell.js";
import { Avatar } from "../components/Avatar.js";
import { FranchiseBadge } from "../components/FranchiseBadge.js";
import { Button, Field, inputStyle, Notice } from "../components/ui.js";
import {
  appointCaptain,
  CATEGORY_REFUSAL,
  clearFranchiseLogo,
  createFranchise,
  deleteFranchise,
  fetchFranchises,
  Refusal,
  setPoolPurses,
  setSquadPurse,
  updateFranchise,
  uploadFranchiseLogo,
  type FranchiseInput,
} from "../lib/franchises.js";
import { fetchRosters } from "../lib/auction.js";
import type { AuctionCurrency } from "../lib/currency.js";
import { fetchTournaments } from "../lib/tournaments.js";
import type { Franchise, Squad, Tournament, TournamentEvent } from "../lib/types.js";

/**
 * The franchises of an auction tournament, on two screens: the organiser's set-up (below, and
 * on the tournament's Organise page) and a read-only list on the tournament page for everybody
 * else. Both read `fetchFranchises`, which hands members no purses, so the read-only side
 * cannot show one by mistake.
 */

function useSetup(tournament: Tournament) {
  return useQuery({
    queryKey: ["franchises", tournament.id],
    queryFn: () => fetchFranchises(tournament.id),
  });
}

/** Captain, purse: the columns of a pool's squads, shared by the header and each row. The team
 * sits on its own line above, because a team's name needs the width a phone has left. */
const SQUAD_GRID = "grid grid-cols-[minmax(0,1fr)_5.5rem] items-center gap-x-3";

/** A pool's name as the member list shows it: mono, small, uppercase. */
const POOL_LABEL = "font-mono text-[11px] uppercase tracking-[0.06em] text-ink-muted";

function SquadHeader({ currency }: { currency: AuctionCurrency }) {
  return (
    <div aria-hidden className={`${SQUAD_GRID} ${POOL_LABEL} mt-4`}>
      <span>Captain</span>
      <span className="text-right">Purse ({currency})</span>
    </div>
  );
}

function squadFor(
  squads: Squad[],
  franchise: Franchise,
  event: TournamentEvent,
): Squad | undefined {
  return squads.find((s) => s.franchiseId === franchise.id && s.eventId === event.id);
}

/**
 * A team's name, in the display face the scoreboard and tournament banner use. One place, so
 * the member list and the organiser's cards say it the same way.
 */
function TeamName({ franchise, size = 22 }: { franchise: Franchise; size?: number }) {
  return (
    <span
      className="font-display block uppercase"
      style={{ fontSize: size, fontWeight: 800, lineHeight: 1 }}
    >
      {franchise.name}
    </span>
  );
}

/**
 * Members' view: who the teams are, and who captains each pool. No purses, no controls.
 *
 * Ruled rows like the events list. The captains are one wrapping run of pool and name pairs,
 * not a sentence per pool: "captain" is said once, as the label of the run, and each pool is
 * a mono label with the name beside it, the way event facts are. Each row opens the
 * franchise's own page, where its squads are.
 */
export function FranchiseList({ tournament }: { tournament: Tournament }) {
  const { data } = useSetup(tournament);
  if (!tournament.auction || !data || data.franchises.length === 0) return null;

  return (
    <section className="mt-6 border-t-2 pt-4" style={{ borderColor: "var(--color-line)" }}>
      <h2 className="micro mb-1">Teams</h2>
      <ul>
        {data.franchises.map((franchise) => {
          const captains = tournament.events.flatMap((event) => {
            const captain = squadFor(data.squads, franchise, event)?.captain;
            return captain ? [{ event, captain }] : [];
          });

          return (
            <li
              key={franchise.id}
              className="border-b"
              style={{ borderColor: "var(--color-line-muted)" }}
            >
              <Link
                to={`/tournaments/${tournament.id}/franchises/${franchise.id}`}
                className="flex items-start gap-3 py-3"
              >
                <FranchiseBadge franchise={franchise} size={40} />
                <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                  <TeamName franchise={franchise} />
                  {franchise.tagline ? (
                    <span className="font-mono text-[11px] text-ink-muted">
                      {franchise.tagline}
                    </span>
                  ) : null}
                  {captains.length > 0 ? (
                    <div className="font-mono flex flex-wrap items-baseline gap-x-4 gap-y-0.5 text-[11px]">
                      <span className="font-semibold uppercase tracking-[0.12em]">Captains</span>
                      <dl className="m-0 contents">
                        {captains.map(({ event, captain }) => (
                          <div key={event.id} className="flex gap-1.5">
                            <dt className="uppercase tracking-[0.06em] text-ink-muted">
                              {event.name}
                            </dt>
                            <dd className="m-0 font-semibold">{captain.displayName}</dd>
                          </div>
                        ))}
                      </dl>
                    </div>
                  ) : null}
                </div>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  aria-hidden
                  className="shrink-0 self-center"
                >
                  <path d="M9 5l7 7-7 7" />
                </svg>
              </Link>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/**
 * One franchise, at a URL of its own, for everybody who can see the tournament: its identity,
 * then each pool in turn. A finished pool shows the squad, captain first; a pool still to be
 * auctioned shows only its captain, because the database sends members no players for it yet.
 * Nothing about money: members get no prices or purses, and this page asks for neither.
 */
export function FranchisePage() {
  const { tournamentId, franchiseId } = useParams<{
    tournamentId: string;
    franchiseId: string;
  }>();
  const tournaments = useQuery({
    queryKey: ["tournaments"],
    queryFn: fetchTournaments,
  });
  const tournament = tournaments.data?.find((candidate) => candidate.id === tournamentId);
  const setup = useQuery({
    queryKey: ["franchises", tournamentId],
    queryFn: () => fetchFranchises(tournamentId ?? ""),
    enabled: Boolean(tournament?.auction),
  });
  const finished = (tournament?.events ?? []).filter((event) => event.auctionFinishedAt !== null);
  const ids = finished.map((event) => event.id);
  const { data: rosters = [] } = useQuery({
    queryKey: ["rosters", tournamentId, ids],
    queryFn: () => fetchRosters(ids),
    enabled: Boolean(tournament?.auction) && ids.length > 0,
  });

  if (tournaments.isPending || (tournament?.auction && setup.isPending)) {
    return (
      <main className="mx-auto max-w-2xl px-4 pb-16 pt-6">
        <p className="micro">Loading…</p>
      </main>
    );
  }

  if (tournament && !tournament.auction) {
    return <Navigate to={`/tournaments/${tournament.id}`} replace />;
  }

  const franchise = setup.data?.franchises.find((candidate) => candidate.id === franchiseId);
  if (tournaments.error || setup.error || !tournament || !franchise) {
    return (
      <main className="mx-auto max-w-2xl px-4 pb-16 pt-6">
        <Link
          to={tournament ? `/tournaments/${tournament.id}` : "/events"}
          className="micro mb-4 inline-block underline"
        >
          ← {tournament ? tournament.name : "All events"}
        </Link>
        <Notice tone="attention">
          {tournaments.error || setup.error
            ? "Couldn't load this team. Try again."
            : "That team isn't here any more."}
        </Notice>
      </main>
    );
  }

  return (
    <main className="mx-auto max-w-2xl px-4 pb-16 pt-6">
      <Link to={`/tournaments/${tournament.id}`} className="micro mb-4 inline-block underline">
        ← {tournament.name}
      </Link>
      <PageHeader eyebrow={`Team · ${tournament.name}`} title={franchise.name} />
      <div className="-mt-2 mb-6 flex items-center gap-3">
        <FranchiseBadge franchise={franchise} size={56} />
        {franchise.tagline ? (
          <span className="font-mono text-[11px] text-ink-muted">{franchise.tagline}</span>
        ) : null}
      </div>

      {tournament.events.map((event) => {
        const squad = squadFor(setup.data!.squads, franchise, event);
        const done = event.auctionFinishedAt !== null;
        const players = squad ? rosters.filter((r) => r.squadId === squad.id) : [];
        return (
          <section
            key={event.id}
            aria-label={`${event.name} squad`}
            className="border-t-2 py-4"
            style={{ borderColor: franchise.colour ?? "var(--color-line)" }}
          >
            <h2 className="micro mb-2">{event.name}</h2>
            <ul className="flex flex-col gap-1.5">
              {squad?.captain ? (
                <li className="flex items-center gap-2 text-[15px]">
                  <Avatar
                    name={squad.captain.displayName}
                    url={squad.captain.avatarUrl}
                    size={24}
                  />
                  {squad.captain.displayName} <span className="micro">captain</span>
                </li>
              ) : (
                <li className="text-sm text-ink-muted">No captain yet.</li>
              )}
              {players.map((player) => (
                <li key={player.memberId} className="flex items-center gap-2 text-[15px]">
                  <Avatar name={player.displayName} url={player.avatarUrl} size={24} />
                  {player.displayName}
                </li>
              ))}
            </ul>
            {done ? null : (
              <p className="font-mono mt-2 text-[11px] text-ink-muted">
                Players are named once this pool&rsquo;s auction is finished
              </p>
            )}
          </section>
        );
      })}
    </main>
  );
}

/** The organiser's set-up: franchises, their captains and purses. */
export function FranchiseManager({ tournament }: { tournament: Tournament }) {
  const queryClient = useQueryClient();
  const { data, error, isPending } = useSetup(tournament);
  const locked = tournament.auctionStartedAt !== null;
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["franchises", tournament.id] });

  if (isPending) return <p className="micro mt-2">Loading…</p>;
  if (error || !data)
    return <Notice tone="attention">Couldn&rsquo;t load the teams. Try again.</Notice>;

  return (
    <div className="mt-3">
      {locked ? (
        <Notice>
          The auction has started, so the teams are fixed. Logos, colours and taglines can still
          change.
        </Notice>
      ) : null}

      {data.franchises.length === 0 ? (
        <p className="text-sm text-ink-muted">No teams yet.</p>
      ) : (
        <ul className="flex flex-col gap-6">
          {data.franchises.map((franchise) => (
            <FranchiseCard
              key={franchise.id}
              franchise={franchise}
              locked={locked}
              onChanged={refresh}
            />
          ))}
        </ul>
      )}

      {!locked ? <AddFranchise tournamentId={tournament.id} onAdded={refresh} /> : null}
    </div>
  );
}

/**
 * One pool's squads: each team's captain and purse in it, and one purse for every team. The
 * organiser's half of a pool's set-up, on the pool's own Squads page. It used to be a column of
 * every team's card on the teams page, five pools deep.
 */
export function PoolSquads({
  tournament,
  event,
}: {
  tournament: Tournament;
  event: TournamentEvent;
}) {
  const queryClient = useQueryClient();
  const { data, error, isPending } = useSetup(tournament);
  const locked = tournament.auctionStartedAt !== null;
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["franchises", tournament.id] });

  if (isPending) return <p className="micro mt-2">Loading…</p>;
  if (error || !data)
    return <Notice tone="attention">Couldn&rsquo;t load the teams. Try again.</Notice>;

  if (data.franchises.length === 0) {
    return (
      <p className="text-sm text-ink-muted">
        No teams yet.{" "}
        <Link to={`/tournaments/${tournament.id}/teams`} className="underline">
          Add them
        </Link>{" "}
        first.
      </p>
    );
  }

  return (
    <div>
      {locked ? <Notice>The auction has started, so captains and purses are fixed.</Notice> : null}

      <SquadHeader currency={tournament.auctionCurrency} />
      <ul className="mt-3 flex flex-col gap-5">
        {data.franchises.map((franchise) => {
          const squad = squadFor(data.squads, franchise, event);
          return squad ? (
            <SquadRow
              key={franchise.id}
              squad={squad}
              event={event}
              name={franchise.name}
              label={
                <span className="flex min-w-0 items-center gap-2">
                  <FranchiseBadge franchise={franchise} size={24} />
                  <span className="truncate text-[15px] font-bold">{franchise.name}</span>
                </span>
              }
              locked={locked}
              onChanged={refresh}
            />
          ) : null;
        })}
      </ul>

      <div className="mt-8 border-t-2 pt-4" style={{ borderColor: "var(--color-line)" }}>
        <h2 className="micro mb-2">Same purse for every team</h2>
        <ul>
          <PoolPurse
            event={event}
            currency={tournament.auctionCurrency}
            locked={locked}
            onChanged={refresh}
          />
        </ul>
      </div>
    </div>
  );
}

function AddFranchise({ tournamentId, onAdded }: { tournamentId: string; onAdded: () => void }) {
  const [name, setName] = useState("");
  const add = useMutation({
    mutationFn: () =>
      createFranchise(tournamentId, {
        name: name.trim(),
        shortName: null,
        colour: null,
        tagline: null,
      }),
    onSuccess: () => {
      setName("");
      onAdded();
    },
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (name.trim()) add.mutate();
  };

  return (
    <form onSubmit={submit} className="mt-6 flex flex-wrap items-center gap-3">
      <input
        aria-label="New team name"
        placeholder="New team name"
        maxLength={40}
        value={name}
        onChange={(event) => setName(event.target.value)}
        className="min-w-0 flex-1"
        style={inputStyle}
      />
      <Button type="submit" tone="primary" disabled={add.isPending || !name.trim()}>
        Add team
      </Button>
      {add.error ? (
        <div className="w-full">
          <Notice tone="attention">{add.error.message}</Notice>
        </div>
      ) : null}
    </form>
  );
}

function FranchiseCard({
  franchise,
  locked,
  onChanged,
}: {
  franchise: Franchise;
  locked: boolean;
  onChanged: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const remove = useMutation({
    mutationFn: () => deleteFranchise(franchise.id),
    onSuccess: onChanged,
  });

  return (
    <li className="border-t pt-4" style={{ borderColor: "var(--color-line-muted)" }}>
      <div className="flex items-center gap-3">
        <FranchiseBadge franchise={franchise} />
        <div className="min-w-0 flex-1">
          <TeamName franchise={franchise} />
          {franchise.tagline ? (
            <div className="font-mono mt-1 text-[11px] text-ink-muted">{franchise.tagline}</div>
          ) : null}
        </div>
        <Button type="button" tone="quiet" onClick={() => setEditing((open) => !open)}>
          {editing ? "Done" : "Edit"}
        </Button>
      </div>

      {editing ? (
        <FranchiseEditor franchise={franchise} locked={locked} onChanged={onChanged} />
      ) : null}

      {!locked ? (
        <div className="mt-3">
          {confirmRemove ? (
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-sm">Remove {franchise.name} and its squads?</span>
              <Button
                type="button"
                tone="attention"
                disabled={remove.isPending}
                onClick={() => remove.mutate()}
              >
                Remove
              </Button>
              <Button type="button" tone="quiet" onClick={() => setConfirmRemove(false)}>
                Keep
              </Button>
            </div>
          ) : (
            <Button type="button" tone="quiet" onClick={() => setConfirmRemove(true)}>
              Remove team
            </Button>
          )}
          {remove.error ? <Notice tone="attention">{remove.error.message}</Notice> : null}
        </div>
      ) : null}
    </li>
  );
}

function FranchiseEditor({
  franchise,
  locked,
  onChanged,
}: {
  franchise: Franchise;
  locked: boolean;
  onChanged: () => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [form, setForm] = useState<FranchiseInput>({
    name: franchise.name,
    shortName: franchise.shortName,
    colour: franchise.colour,
    tagline: franchise.tagline,
  });

  const save = useMutation({
    mutationFn: () => updateFranchise(franchise.id, locked ? { ...form, name: undefined } : form),
    onSuccess: onChanged,
  });
  const upload = useMutation({
    mutationFn: (file: File) => uploadFranchiseLogo(franchise.id, file),
    onSuccess: onChanged,
  });
  const clear = useMutation({
    mutationFn: () => clearFranchiseLogo(franchise.id),
    onSuccess: onChanged,
  });
  const busy = save.isPending || upload.isPending || clear.isPending;
  const error = save.error ?? upload.error ?? clear.error;

  return (
    <div className="mt-4">
      <Field label="Name">
        <input
          value={form.name}
          maxLength={40}
          disabled={locked}
          onChange={(event) => setForm({ ...form, name: event.target.value })}
          className="disabled:opacity-40"
          style={inputStyle}
        />
      </Field>
      <div className="flex items-start gap-4">
        <Field label="Short name">
          <input
            value={form.shortName ?? ""}
            maxLength={4}
            onChange={(event) => setForm({ ...form, shortName: event.target.value || null })}
            style={{ ...inputStyle, width: 120 }}
          />
        </Field>
        <Field label="Colour">
          <span className="flex items-center gap-2">
            <input
              type="color"
              value={form.colour ?? "#6b7280"}
              onChange={(event) => setForm({ ...form, colour: event.target.value })}
              style={{ ...inputStyle, width: 64, padding: 4 }}
            />
            {form.colour ? (
              <Button type="button" tone="quiet" onClick={() => setForm({ ...form, colour: null })}>
                None
              </Button>
            ) : null}
          </span>
        </Field>
      </div>
      <Field label="Tagline">
        <input
          value={form.tagline ?? ""}
          maxLength={80}
          onChange={(event) => setForm({ ...form, tagline: event.target.value || null })}
          style={inputStyle}
        />
      </Field>

      <div className="flex flex-wrap gap-3">
        <Button
          type="button"
          tone="primary"
          disabled={busy || !form.name.trim()}
          onClick={() => save.mutate()}
        >
          Save
        </Button>
        <Button type="button" disabled={busy} onClick={() => input.current?.click()}>
          {franchise.logoUrl ? "Replace logo" : "Add logo"}
        </Button>
        {franchise.logoUrl ? (
          <Button type="button" disabled={busy} onClick={() => clear.mutate()}>
            Remove logo
          </Button>
        ) : null}
      </div>
      <input
        ref={input}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = "";
          if (file) upload.mutate(file);
        }}
      />
      {error ? <Notice tone="attention">{error.message}</Notice> : null}
    </div>
  );
}

/** One cell of the grid: a franchise's squad in one pool, with its captain and purse. */
function SquadRow({
  squad,
  event,
  label,
  name,
  locked,
  onChanged,
}: {
  squad: Squad;
  event: TournamentEvent;
  /** Whose squad this is, in the first column: the team, on a pool's Squads page. */
  label: ReactNode;
  /** The same, as words, for the controls' names. */
  name: string;
  locked: boolean;
  onChanged: () => void;
}) {
  const entrants = event.entries.filter((entry) => entry.status === "registered");
  // The category refused somebody and the organiser is deciding whether to override.
  const [pending, setPending] = useState<{
    memberId: string;
    message: string;
  } | null>(null);
  const [reason, setReason] = useState("");

  const appoint = useMutation({
    mutationFn: (input: { memberId: string | null; reason?: string }) =>
      appointCaptain(squad.id, input.memberId, input.reason),
    onSuccess: () => {
      setPending(null);
      setReason("");
      onChanged();
    },
    onError: (error, input) => {
      if (error instanceof Refusal && error.code === CATEGORY_REFUSAL && input.memberId) {
        setPending({ memberId: input.memberId, message: error.message });
      }
    },
  });

  const refused = appoint.error instanceof Refusal && appoint.error.code === CATEGORY_REFUSAL;

  return (
    <li>
      <div className="mb-1.5">{label}</div>
      <div className={SQUAD_GRID}>
        <select
          aria-label={`Captain, ${name}`}
          value={squad.captain?.id ?? ""}
          disabled={locked || appoint.isPending}
          onChange={(change) => {
            setPending(null);
            appoint.mutate({ memberId: change.target.value || null });
          }}
          className="disabled:opacity-40"
          style={inputStyle}
        >
          <option value="">Needs a captain</option>
          {entrants.map((entry) => (
            <option key={entry.memberId} value={entry.memberId}>
              {entry.displayName}
            </option>
          ))}
        </select>
        <PurseInput squad={squad} name={name} locked={locked} onChanged={onChanged} />
      </div>

      {pending ? (
        <div className="mt-2 flex flex-col gap-2">
          <Notice tone="attention">
            {pending.message.charAt(0).toUpperCase() + pending.message.slice(1)}. Appoint them
            anyway?
          </Notice>
          <input
            aria-label="Reason for the override"
            placeholder="Reason"
            value={reason}
            onChange={(change) => setReason(change.target.value)}
            style={inputStyle}
          />
          <div className="flex gap-3">
            <Button
              type="button"
              tone="attention"
              disabled={!reason.trim() || appoint.isPending}
              onClick={() => appoint.mutate({ memberId: pending.memberId, reason })}
            >
              Appoint anyway
            </Button>
            <Button type="button" tone="quiet" onClick={() => setPending(null)}>
              Cancel
            </Button>
          </div>
        </div>
      ) : appoint.error && !refused ? (
        <Notice tone="attention">{appoint.error.message}</Notice>
      ) : null}
    </li>
  );
}

function PurseInput({
  squad,
  name,
  locked,
  onChanged,
}: {
  squad: Squad;
  name: string;
  locked: boolean;
  onChanged: () => void;
}) {
  const [value, setValue] = useState(String(squad.purse ?? 0));
  const save = useMutation({
    mutationFn: (purse: number) => setSquadPurse(squad.id, purse),
    onSuccess: onChanged,
  });

  const commit = () => {
    const purse = Number(value);
    if (Number.isInteger(purse) && purse >= 0 && purse !== squad.purse) save.mutate(purse);
  };

  return (
    <span className="flex flex-col">
      <input
        aria-label={`Purse, ${name}`}
        inputMode="numeric"
        value={value}
        disabled={locked}
        onChange={(event) => setValue(event.target.value.replace(/[^0-9]/g, ""))}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === "Enter") commit();
        }}
        className="disabled:opacity-40"
        style={{ ...inputStyle, textAlign: "right" }}
      />
      {save.error ? <Notice tone="attention">{save.error.message}</Notice> : null}
    </span>
  );
}

/** The same purse for every squad in one pool, in one step. */
function PoolPurse({
  event,
  currency,
  locked,
  onChanged,
}: {
  event: TournamentEvent;
  currency: AuctionCurrency;
  locked: boolean;
  onChanged: () => void;
}) {
  const [value, setValue] = useState("");
  const apply = useMutation({
    mutationFn: (purse: number) => setPoolPurses(event.id, purse),
    onSuccess: () => {
      setValue("");
      onChanged();
    },
  });

  return (
    <li className="flex flex-wrap items-center gap-3">
      <input
        aria-label={`Purse for every team, ${event.name}`}
        placeholder={currency}
        inputMode="numeric"
        value={value}
        disabled={locked}
        onChange={(change) => setValue(change.target.value.replace(/[^0-9]/g, ""))}
        className="disabled:opacity-40"
        style={{ ...inputStyle, width: 88, textAlign: "right" }}
      />
      <Button
        type="button"
        disabled={locked || value === "" || apply.isPending}
        onClick={() => apply.mutate(Number(value))}
      >
        Set for all
      </Button>
      {apply.error ? <Notice tone="attention">{apply.error.message}</Notice> : null}
    </li>
  );
}
